import { Combobox } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { getKmEntityTitle, useKmEntities } from '@/entities/ontology';
import { ListTruncationNotice } from '@/shared/ui';
import type { KmAttrFormValue } from './attrFormValue';

/**
 * Размер выборки справочника-типа: перечень вариантов показывается списком целиком —
 * асинхронного источника опций у `Combobox` нет. За потолком выдача усечена, и поле
 * говорит об этом: иначе недостающий экземпляр читался бы как отсутствующий.
 */
const REF_OPTIONS_LIMIT = 1000;

type Props = {
  /** Код типа сущности, экземпляры которого служат справочником */
  refEntityType: string;
  /** Выбирается несколько значений сразу */
  isMulti?: boolean;
  value?: KmAttrFormValue;
  onChange?: (value: KmAttrFormValue) => void;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
};

/**
 * Выбор значения из справочника экземпляров: значение — идентификатор экземпляра,
 * подпись — его название по тому же правилу, что в списке раздела.
 */
export const KmAttrRefInput: FC<Props> = ({
  refEntityType,
  isMulti,
  value,
  onChange,
  disabled,
  error,
  placeholder,
}) => {
  const { data: entitiesPage } = useKmEntities({
    type: [refEntityType],
    includeDeleted: false,
    limit: REF_OPTIONS_LIMIT,
  });

  const selected = useMemo(() => {
    if (isMulti) {
      return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
    }
    return typeof value === 'string' ? value : '';
  }, [isMulti, value]);

  const options = useMemo(() => {
    const items = (entitiesPage?.items ?? []).map((entity) => ({
      value: String(entity.id),
      label: getKmEntityTitle(entity),
    }));

    // Экземпляр, не попавший в выборку справочника, остаётся выбранным: без своей опции
    // Combobox показал бы пустое поле, и сохранение стёрло бы значение
    const known = new Set(items.map((item) => item.value));
    const missing = (Array.isArray(selected) ? selected : [selected])
      .filter((id) => id && !known.has(id))
      .map((id) => ({ value: id, label: `#${id}` }));

    return [...items, ...missing];
  }, [entitiesPage, selected]);

  return (
    <div className="flex w-full flex-col gap-1">
      {isMulti ? (
        <Combobox
          isMulti
          value={Array.isArray(selected) ? selected : []}
          onChange={(next) => onChange?.((Array.isArray(next) ? next : []).map(String))}
          options={options}
          disabled={disabled}
          error={error}
          placeholder={placeholder}
          className="w-full"
        />
      ) : (
        <Combobox
          value={Array.isArray(selected) ? '' : selected}
          onChange={(next) => onChange?.(next === undefined || next === null ? '' : String(next))}
          options={options}
          disabled={disabled}
          error={error}
          placeholder={placeholder}
          className="w-full"
        />
      )}

      <ListTruncationNotice shown={entitiesPage?.items.length ?? 0} total={entitiesPage?.total} className="text-xs" />
    </div>
  );
};
